<template>
  <div
    class="mobile-bar border-t border-border-subtle bg-[rgba(249,246,240,0.9)] text-text-primary backdrop-blur-md"
    :style="mobileBarStyle"
  >
    <button
      v-for="(link, index) in links"
      :key="index"
      type="button"
      class="mobile-bar-icon"
      :aria-label="link.label"
      @click="iconAction(link)"
    >
      <MainIcon size="1.6em" :background="false" :icon="link.icon" color="currentColor" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import MainIcon from '../general/MainIcon.vue';

interface IMobileBarLink {
  url: string;
  icon: string;
  label: string;
}

export default defineComponent({
  name: 'MobileBar',

  components: {
    MainIcon,
  },

  data() {
    return {
      viewportTop: null as number | null,
      positionFrame: 0,
    };
  },

  mounted() {
    this.scheduleMobileBarPositionUpdate();
    window.addEventListener('resize', this.scheduleMobileBarPositionUpdate);
    window.addEventListener('scroll', this.scheduleMobileBarPositionUpdate, { passive: true });
    window.visualViewport?.addEventListener('resize', this.scheduleMobileBarPositionUpdate);
    window.visualViewport?.addEventListener('scroll', this.scheduleMobileBarPositionUpdate);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.scheduleMobileBarPositionUpdate);
    window.removeEventListener('scroll', this.scheduleMobileBarPositionUpdate);
    window.visualViewport?.removeEventListener('resize', this.scheduleMobileBarPositionUpdate);
    window.visualViewport?.removeEventListener('scroll', this.scheduleMobileBarPositionUpdate);

    if (this.positionFrame) {
      cancelAnimationFrame(this.positionFrame);
    }
  },

  computed: {
    user(): boolean {
      return this.$store.getters['user/getUser'];
    },

    hasActiveEvent(): boolean {
      return this.$store.getters['event/hasActiveEvent'];
    },

    isMenuOpen(): boolean {
      return this.$store.getters['app/getMenuState'];
    },

    menuIcon(): string {
      return this.isMenuOpen ? 'close' : 'menu';
    },

    links(): IMobileBarLink[] {
      return this.hasActiveEvent ? this.subscribedLinks : this.baseLinks;
    },

    subscribedLinks(): IMobileBarLink[] {
      return [
        { url: '?menu', icon: this.menuIcon, label: 'תפריט' },
        { url: '/contact-us', icon: 'contact_support', label: 'צור קשר' },
        { url: '/event', icon: 'event', label: 'האירוע' },
        { url: '/', icon: 'house', label: 'בית' },
        { url: '/profile', icon: 'account_circle', label: 'פרופיל' },
      ];
    },

    baseLinks(): IMobileBarLink[] {
      const links: IMobileBarLink[] = [
        { url: '?menu', icon: this.menuIcon, label: 'תפריט' },
        { url: '/order', icon: 'storefront', label: 'הזמנה' },
        { url: '/', icon: 'house', label: 'בית' },
        { url: '/contact-us', icon: 'contact_support', label: 'צור קשר' },
      ];

      if (this.user) {
        links.push({ url: '/profile', icon: 'account_circle', label: 'פרופיל' });
      } else {
        links.push({ url: '/login', icon: 'account_circle', label: 'התחבר' });
      }

      return links;
    },

    mobileBarStyle(): Record<string, string> {
      if (this.viewportTop === null) {
        return {
          bottom: '0',
        };
      }

      return {
        top: `${this.viewportTop}px`,
        bottom: 'auto',
      };
    },
  },

  methods: {
    scheduleMobileBarPositionUpdate() {
      if (this.positionFrame) {
        cancelAnimationFrame(this.positionFrame);
      }

      this.positionFrame = requestAnimationFrame(this.updateMobileBarPosition);
    },

    updateMobileBarPosition() {
      this.positionFrame = 0;

      const viewport = window.visualViewport;
      if (!viewport) {
        this.viewportTop = null;
        return;
      }

      const barHeight = (this.$el as HTMLElement)?.offsetHeight || 60;
      this.viewportTop = Math.max(0, viewport.offsetTop + viewport.height - barHeight);
    },

    iconAction(link: IMobileBarLink) {
      if (link.url.includes('menu')) {
        this.$store.dispatch('app/toggleMenu');
        return;
      }

      this.$router.push(link.url);
    },
  },
});
</script>

<style lang="scss" scoped>
.mobile-bar {
  box-sizing: border-box;
  height: calc(60px + env(safe-area-inset-bottom, 0px));
  width: 100%;
  padding: 0 20px env(safe-area-inset-bottom, 0px);
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10000;
  transform: translateZ(0);
  will-change: top;

  .mobile-bar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: opacity 600ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>
